<div class="group-call-container" [class.pass-through]="isMinimized">
  <!-- Full Screen View -->
  <div class="fullscreen" [class.hidden]="isMinimized">
    <div class="top-right-controls">
      <button
        class="btn btn-secondary minimize-btn"
        (click)="toggleMinimize()"
        [title]="'video-call.minimize' | translate"
      >
        &#x2015;
      </button>
    </div>

    <!-- Video Grid -->
    <div class="video-grid" [ngClass]="gridClass">
      <!-- Local Participant -->
      <div class="grid-item" *ngIf="localParticipant">
        <app-participant-tile
          [participant]="localParticipant"
          [isLocal]="true"
        ></app-participant-tile>
      </div>

      <!-- Remote Participants -->
      <div class="grid-item" *ngFor="let rp of remoteParticipants">
        <app-participant-tile
          [participant]="rp"
          [isLocal]="false"
        ></app-participant-tile>
      </div>
    </div>

    <!-- Floating Controls -->
    <div class="controls">
      <div class="left">
        <span class="duration">{{ duration }}</span>
      </div>
      <div class="center">
        <div class="center-stack">
          <div class="permission-hint" *ngIf="permissionInfoKey as key">
            {{ key | translate }}
          </div>
          <div class="button-row">
            <button
              class="btn btn-icon btn-primary amts-bg-el23"
              (click)="toggleMute()"
              [disabled]="!canSendAudio"
              [attr.aria-disabled]="!canSendAudio"
              [attr.title]="
                !canSendAudio
                  ? ('call.permission.partialAudio' | translate)
                  : (isMuted ? 'group-call.unmute' : 'group-call.mute')
                    | translate
              "
            >
              <img [src]="muteIcon" alt="" />
            </button>

            <button
              *ngIf="callType === 2"
              class="btn btn-icon btn-primary amts-bg-el23"
              (click)="toggleVideo()"
              [disabled]="!canSendVideo"
              [attr.aria-disabled]="!canSendVideo"
              [attr.title]="
                !canSendVideo
                  ? ('call.permission.partialVideo' | translate)
                  : (isVideoOff ? 'group-call.camera-on' : 'group-call.camera-off')
                    | translate
              "
            >
              <img [src]="cameraIcon" alt="" />
            </button>

            <button
              class="btn btn-icon btn-primary amts-bg-el23 hangup-btn"
              (click)="leaveCall()"
              [title]="'group-call.leave-call' | translate"
            >
              <img [src]="hangupIcon" alt="" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Minimized View -->
  <div
    class="minimized"
    [class.hidden]="!isMinimized"
    [ngStyle]="minimizedStyle"
    (mousedown)="onMinMouseDown($event)"
  >
    <!-- Resize Handles -->
    <div
      class="resize-handle resize-top"
      (mousedown)="onResizeMouseDown($event, 'top')"
    ></div>
    <div
      class="resize-handle resize-right"
      (mousedown)="onResizeMouseDown($event, 'right')"
    ></div>
    <div
      class="resize-handle resize-bottom"
      (mousedown)="onResizeMouseDown($event, 'bottom')"
    ></div>
    <div
      class="resize-handle resize-left"
      (mousedown)="onResizeMouseDown($event, 'left')"
    ></div>
    <div
      class="resize-handle resize-top-left"
      (mousedown)="onResizeMouseDown($event, 'top-left')"
    ></div>
    <div
      class="resize-handle resize-top-right"
      (mousedown)="onResizeMouseDown($event, 'top-right')"
    ></div>
    <div
      class="resize-handle resize-bottom-left"
      (mousedown)="onResizeMouseDown($event, 'bottom-left')"
    ></div>
    <div
      class="resize-handle resize-bottom-right"
      (mousedown)="onResizeMouseDown($event, 'bottom-right')"
    ></div>

    <div class="min-content">
      <video
        #minVideo
        autoplay
        playsinline
        [muted]="true"
        class="min-video"
      ></video>

      <div class="min-overlay">
        <div class="min-top">
          <span class="duration">{{ duration }}</span>
          <ng-container *ngIf="minimizedParticipant as participant">
            <div
              class="min-status-inline"
              *ngIf="
                !participant.isMicEnabled ||
                (callType === 2 && !participant.isCameraEnabled)
              "
            >
              <span
                class="min-status-icon"
                *ngIf="!participant.isMicEnabled"
                [attr.title]="'video-call.mute' | translate"
              >
                <img src="/assets/icons/mic-off.svg" alt="" />
              </span>
              <span
                class="min-status-icon"
                *ngIf="callType === 2 && !participant.isCameraEnabled"
                [attr.title]="'video-call.camera-off' | translate"
              >
                <img src="/assets/icons/camera-off.svg" alt="" />
              </span>
            </div>
          </ng-container>
          <button
            class="btn btn-sm btn-light btn-restore"
            (mousedown)="$event.stopPropagation()"
            (click)="toggleMinimize(); $event.stopPropagation()"
          >
            ⤢
          </button>
        </div>

        <div class="min-bottom">
          <div class="min-peer-info" *ngIf="minimizedParticipant as participant">
            <div class="min-peer-name">
              {{ participant.name | doctorName: participant.professionalTitle }}
            </div>
            <div class="min-peer-branch" *ngIf="participant.branchName as branch">
              {{ "profile.branch" | translate }}: {{ branch }}
            </div>
          </div>

          <div class="min-actions">
            <button
              class="btn btn-sm btn-icon btn-primary"
              (mousedown)="$event.stopPropagation()"
              (click)="toggleMute(); $event.stopPropagation()"
              [disabled]="!canSendAudio"
              [attr.aria-disabled]="!canSendAudio"
            >
              <img [src]="muteIcon" alt="" />
            </button>

            <button
              *ngIf="callType === 2"
              class="btn btn-sm btn-icon btn-primary"
              (mousedown)="$event.stopPropagation()"
              (click)="toggleVideo(); $event.stopPropagation()"
              [disabled]="!canSendVideo"
              [attr.aria-disabled]="!canSendVideo"
            >
              <img [src]="cameraIcon" alt="" />
            </button>

            <button
              class="btn btn-sm btn-icon btn-primary hangup-btn"
              (mousedown)="$event.stopPropagation()"
              (click)="leaveCall(); $event.stopPropagation()"
            >
              <img [src]="hangupIcon" alt="" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
